import { defineStore } from 'pinia'
import { ref } from 'vue'
import {
    updateAvailability,
    updateCallState,
    updateEndpointCallState,
    updateChatState,
    getAwayReasons,
} from '@/modules/user-profile/availabilityApi'
import useToast from '@/shared/toast/useToast'

export const useAvailabilityStore = defineStore('availability', () => {
    const { showSuccess, showError } = useToast()

    const status = ref(null)
    const awayReason = ref(null)
    const callState = ref(null)
    const chatState = ref(null)

    const endpointAvailability = ref([])

    const awayReasons = ref([])
    const loading = ref(false)

    async function fetchAwayReasons() {
        try {
            const { data } = await getAwayReasons()
            awayReasons.value = data?.data || data || []
        } catch (err) {
            console.error('Failed to fetch away reasons:', err)
        }
    }

    function extractAvailability(response) {
        const body = response?.data
        if (body?.data?.status) return body.data
        if (body?.status) return body
        return null
    }

    async function setOnline() {
        loading.value = true
        try {
            const response = await updateAvailability({
                status: 'online',
                call_state: 'online',
                chat_state: 'online',
            })
            const result = extractAvailability(response)
            if (result) {
                applyState(result)
            } else {
                status.value = 'online'
                awayReason.value = null
                callState.value = 'online'
                chatState.value = 'online'
            }
            showSuccess('You are now online')
        } catch (err) {
            showError(err, 'Failed to go online')
            throw err
        } finally {
            loading.value = false
        }
    }

    async function setAway(reasonId = null, msg = null) {
        loading.value = true
        try {
            const payload = { status: 'away' }
            if (reasonId) payload.away_reason_id = reasonId
            if (msg) payload.message = msg

            const response = await updateAvailability(payload)
            const result = extractAvailability(response)
            if (result) {
                applyState(result)
            } else {
                status.value = 'away'
                awayReason.value = reasonId
                    ? awayReasons.value.find((r) => r.id === reasonId) || null
                    : null
                callState.value = 'away'
                chatState.value = 'away'
            }
            const reason = awayReason.value?.name
            showSuccess(reason ? `Away — ${reason}` : 'You are now away')
        } catch (err) {
            showError(err, 'Failed to set away')
            throw err
        } finally {
            loading.value = false
        }
    }

    async function toggleCall(newState) {
        const prev = callState.value
        loading.value = true
        try {
            const response = await updateCallState({ call_state: newState })
            const result = extractAvailability(response)
            if (result) {
                applyState(result)
            } else {
                callState.value = newState
            }
            const label = newState === 'online' ? 'on' : 'off'
            showSuccess(`Call ${label}`)
        } catch (err) {
            callState.value = prev
            showError(err, 'Failed to update call')
            throw err
        } finally {
            loading.value = false
        }
    }

    async function toggleChat(newState) {
        const prev = chatState.value
        loading.value = true
        try {
            const response = await updateChatState({ chat_state: newState })
            const result = extractAvailability(response)
            if (result) {
                applyState(result)
            } else {
                chatState.value = newState
            }
            const label = newState === 'online' ? 'on' : 'off'
            showSuccess(`Chat ${label}`)
        } catch (err) {
            chatState.value = prev
            showError(err, 'Failed to update chat')
            throw err
        } finally {
            loading.value = false
        }
    }

    function getEndpointState(endpointId) {
        const ep = endpointAvailability.value.find((e) => String(e.endpoint_id) === String(endpointId))
        return ep?.call_state || 'online'
    }

    async function toggleEndpointCall(endpointId, newState) {
        const ep = endpointAvailability.value.find((e) => String(e.endpoint_id) === String(endpointId))
        const prev = ep?.call_state || 'online'
        if (ep) ep.call_state = newState
        try {
            await updateEndpointCallState(endpointId, newState)
            const label = newState === 'online' ? 'accepting calls' : 'paused'
            showSuccess(`Endpoint ${label}`)
        } catch (err) {
            if (ep) ep.call_state = prev
            showError(err, 'Failed to update endpoint')
            throw err
        }
    }

    function applyState(data) {
        status.value = data.status || null
        awayReason.value = data.away_reason || null
        callState.value = data.call_state || null
        chatState.value = data.chat_state || null
        if (data.endpoint_availability) {
            endpointAvailability.value = data.endpoint_availability
        }
    }

    // Sync status from another device's change (own-id lc:member_updated).
    // The event only carries is_away, so call/chat states are left alone;
    // the away reason is kept when going away (unknown here) and cleared on
    // online — mirroring the server, which nulls away_reason_id on online.
    function syncStatus(newStatus) {
        status.value = newStatus
        if (newStatus === 'online') {
            awayReason.value = null
        }
    }

    return {
        status,
        awayReason,
        callState,
        chatState,
        endpointAvailability,
        awayReasons,
        loading,

        fetchAwayReasons,
        setOnline,
        setAway,
        toggleCall,
        toggleChat,
        getEndpointState,
        toggleEndpointCall,
        applyState,
        syncStatus,
    }
})

export default useAvailabilityStore
